<template>
  <div class="edit-seasonal-compact form-group">
    <label class="form-label">Seasonal Availability</label>
    <div class="seasonal-container">
      <!-- Year-round chip or selected months -->
      <template v-if="isYearRound">
        <span class="tag-chip tag-success">
          Year-round
          <span class="tag-remove" @click="setNotYearRound">&times;</span>
        </span>
      </template>

      <template v-else>
        <span
          v-for="month in selectedMonthsWithLabels"
          :key="month.id"
          class="tag-chip">
          {{ month.short }}
          <span class="tag-remove" @click="removeMonth(month.id)">&times;</span>
        </span>
      </template>

      <!-- Add button with dropdown -->
      <b-dropdown
        v-model="dropdownOpen"
        aria-role="menu"
        position="is-bottom-left"
        :scrollable="true"
        :max-height="350"
        trap-focus
        append-to-body>
        <template #trigger>
          <button class="add-tag-button" type="button">
            <b-icon icon="plus" size="is-small" />
            <span>Add</span>
          </button>
        </template>

        <!-- Year-round option -->
        <b-dropdown-item
          :disabled="isYearRound"
          @click="setYearRound">
          <b-icon icon="check-circle" size="is-small" class="mr-1" />
          Year-round (all months)
        </b-dropdown-item>

        <hr class="dropdown-divider">

        <!-- Quick select by season -->
        <b-dropdown-item custom class="dropdown-category-header">
          <strong>Quick Select</strong>
        </b-dropdown-item>
        <b-dropdown-item
          v-for="season in seasons"
          :key="season.id + '_quick'"
          @click="selectSeason(season.id)">
          <b-icon icon="weather-sunny" size="is-small" class="mr-1" v-if="season.id === 'summer'" />
          <b-icon icon="leaf" size="is-small" class="mr-1" v-else-if="season.id === 'autumn'" />
          <b-icon icon="snowflake" size="is-small" class="mr-1" v-else-if="season.id === 'winter'" />
          <b-icon icon="flower" size="is-small" class="mr-1" v-else />
          {{ season.label }}
        </b-dropdown-item>

        <hr class="dropdown-divider">

        <!-- Individual months grouped by season -->
        <template v-for="season in seasons">
          <b-dropdown-item :key="season.id + '_header'" custom class="dropdown-category-header">
            <strong>{{ season.label }}</strong>
          </b-dropdown-item>
          <b-dropdown-item
            v-for="month in season.monthData"
            :key="month.id"
            :disabled="isMonthSelected(month.id)"
            @click="addMonth(month.id)">
            <span :class="{ 'has-text-grey': isMonthSelected(month.id) }">
              {{ month.label }}
              <b-icon v-if="isMonthSelected(month.id)" icon="check" size="is-small" class="ml-1" />
            </span>
          </b-dropdown-item>
        </template>

        <hr class="dropdown-divider">

        <b-dropdown-item @click="clearAll" v-if="selectedMonths.length > 0">
          <b-icon icon="close-circle" size="is-small" class="mr-1" />
          Clear all
        </b-dropdown-item>
      </b-dropdown>
    </div>
  </div>
</template>

<script>
import { getMonthsBySeason, MONTHS, SEASONS } from '@/data/seasonalData'

export default {
  name: 'EditSeasonalAvailability',
  props: {
    value: {
      type: Object,
      default: () => ({
        year_round: true,
        months: []
      })
    }
  },
  data() {
    return {
      dropdownOpen: false
    }
  },
  computed: {
    seasons() {
      return getMonthsBySeason()
    },
    isYearRound: {
      get() {
        return this.value?.year_round ?? true
      },
      set(val) {
        this.emitUpdate({ year_round: val, months: val ? [] : this.selectedMonths })
      }
    },
    selectedMonths: {
      get() {
        return this.value?.months || []
      },
      set(val) {
        this.emitUpdate({ year_round: false, months: val })
      }
    },
    selectedMonthsWithLabels() {
      // Sort by order for display
      return this.selectedMonths
        .map(monthId => MONTHS[monthId])
        .filter(m => m != null)
        .sort((a, b) => a.order - b.order)
    }
  },
  methods: {
    emitUpdate(newValue) {
      this.$emit('input', newValue)
    },
    isMonthSelected(monthId) {
      return this.selectedMonths.includes(monthId)
    },
    addMonth(monthId) {
      if (!this.isMonthSelected(monthId)) {
        this.emitUpdate({
          year_round: false,
          months: [...this.selectedMonths, monthId]
        })
      }
    },
    removeMonth(monthId) {
      this.emitUpdate({
        year_round: false,
        months: this.selectedMonths.filter(m => m !== monthId)
      })
    },
    selectSeason(seasonId) {
      const season = SEASONS[seasonId]
      if (!season) return

      const months = [...this.selectedMonths]
      season.months.forEach(monthId => {
        if (!months.includes(monthId)) {
          months.push(monthId)
        }
      })

      this.emitUpdate({ year_round: false, months })
    },
    setYearRound() {
      this.emitUpdate({ year_round: true, months: [] })
    },
    setNotYearRound() {
      this.emitUpdate({ year_round: false, months: [] })
    },
    clearAll() {
      this.emitUpdate({ year_round: false, months: [] })
    }
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 22px;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.seasonal-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: var(--feeds-green-light);
  color: var(--feeds-green);
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s ease;
}

.tag-chip.tag-success {
  background: linear-gradient(135deg, #00857a 0%, #00a896 100%);
  color: white;
}

.tag-chip:hover {
  background: #d0ece9;
}

.tag-chip.tag-success:hover {
  background: linear-gradient(135deg, #006b62 0%, #008a80 100%);
}

.tag-remove {
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s;
  font-size: 16px;
  line-height: 1;
}

.tag-remove:hover {
  opacity: 1;
}

.add-tag-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px dashed var(--border-medium);
  border-radius: 20px;
  background: transparent;
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.add-tag-button:hover {
  border-color: var(--feeds-green);
  color: var(--feeds-green);
  background: var(--feeds-green-light);
}

.dropdown-category-header {
  background-color: #f5f5f5;
  padding: 0.5rem 1rem !important;
  cursor: default;
}

.dropdown-category-header:hover {
  background-color: #f5f5f5 !important;
}
</style>
